/**
 * Parses exercise bodies that use [gap] syntax while ignoring Markdown
 * link/image brackets: [label](url) and ![alt](url), and voice markers [$text$].
 */

function isMarkdownLinkBracket(text, closeBracketIndex) {
    let j = closeBracketIndex + 1;
    while (j < text.length && /\s/.test(text[j])) j++;
    return text[j] === '(';
}

function findClosingParen(text, openParenIndex) {
    let depth = 0;
    for (let j = openParenIndex; j < text.length; j++) {
        if (text[j] === '(') depth++;
        else if (text[j] === ')') {
            depth--;
            if (depth === 0) return j + 1;
        }
    }
    return -1;
}

/** `[$spoken text$]` is always TTS audio, never a fill-gap / multiple-choice bracket. */
function isVoiceMarkerInner(inner) {
    const s = (inner || '').trim();
    return s.length >= 2 && s.startsWith('$') && s.endsWith('$');
}

/** @returns {Generator<{type:'text', text:string}|{type:'bracket', inner:string}>} */
function* iterateBodyBrackets(body) {
    const text = body || '';
    let i = 0;
    while (i < text.length) {
        const open = text.indexOf('[', i);
        if (open === -1) {
            if (i < text.length) yield { type: 'text', text: text.slice(i) };
            break;
        }
        const close = text.indexOf(']', open + 1);
        if (open > i) {
            let textEnd = open;
            if (open > 0 && text[open - 1] === '!' && close !== -1 && isMarkdownLinkBracket(text, close)) {
                textEnd = open - 1;
            }
            if (textEnd > i) yield { type: 'text', text: text.slice(i, textEnd) };
        }

        if (close === -1) {
            yield { type: 'text', text: text.slice(open) };
            break;
        }

        if (isMarkdownLinkBracket(text, close)) {
            let segStart = open;
            if (open > 0 && text[open - 1] === '!') segStart = open - 1;
            let end = close + 1;
            while (end < text.length && /\s/.test(text[end])) end++;
            if (text[end] === '(') {
                const afterUrl = findClosingParen(text, end);
                if (afterUrl !== -1) {
                    yield { type: 'text', text: text.slice(segStart, afterUrl) };
                    i = afterUrl;
                    continue;
                }
            }
        }

        const inner = text.slice(open + 1, close);
        if (isVoiceMarkerInner(inner)) {
            // Leave intact for formatInline → 🔊 TTS button
            yield { type: 'text', text: text.slice(open, close + 1) };
            i = close + 1;
            continue;
        }

        yield { type: 'bracket', inner };
        i = close + 1;
    }
}

export function parseFillGapInner(inner) {
    const bits = (inner || '').split('/');
    let prompt = '';
    let answers = [];

    if (bits.length === 1) {
        const val = bits[0].trim();
        if (/^[\._]+$/.test(val) || val === '') {
            prompt = val;
            answers = [];
        } else {
            prompt = '';
            answers = [bits[0].trim()];
        }
    } else {
        prompt = bits[0];
        answers = bits.slice(1).filter(a => a !== '').map(a => a.trim());
    }
    return { prompt, answers };
}

/** If a gap was written as `**[answer]**`, drop the leftover wrap markers on either side. */
function peelEmphasisWrapAroundWidgets(parts) {
    const out = (parts || []).map((p) => (p.type === 'text' ? { ...p, text: p.text } : p));
    for (let i = 0; i < out.length; i++) {
        if (out[i].type !== 'gap' && out[i].type !== 'choice' && out[i].type !== 'check') continue;
        const prev = out[i - 1];
        const next = out[i + 1];
        if (prev?.type !== 'text' || next?.type !== 'text') continue;
        if (prev.text.endsWith('**') && next.text.startsWith('**')) {
            prev.text = prev.text.slice(0, -2);
            next.text = next.text.slice(2);
        }
    }
    return out;
}

/** Parse gap syntax in plain text (no markdown tables). */
function parseFillGapsPlainText(body) {
    const parts = [];
    for (const token of iterateBodyBrackets(body)) {
        if (token.type === 'text') {
            parts.push({ type: 'text', text: token.text });
        } else {
            const { prompt, answers } = parseFillGapInner(token.inner);
            parts.push({ type: 'gap', prompt, answers });
        }
    }
    return peelEmphasisWrapAroundWidgets(parts);
}

function isMarkdownTableLine(trimmed, inTable) {
    return (trimmed.startsWith('|') && trimmed.endsWith('|')) || (trimmed.startsWith('|') && inTable);
}

function parseTableRowCells(line) {
    const trimmed = line.trim();
    return trimmed
        .split('|')
        .filter((_, i, arr) => i > 0 && i < arr.length - 1)
        .map(c => c.trim());
}

function isTableSeparatorRow(cells) {
    return cells.length > 0 && cells.every(c => /^[-\s:]+$/.test(c));
}

/** Line immediately before a markdown table: first row is a normal row, not a header. */
export const TABLE_NO_HEADER_MARKER = '{table:no-header}';

export function isTableNoHeaderDirective(line) {
    return String(line || '').trim() === TABLE_NO_HEADER_MARKER;
}

function parseBodyWithMarkdownTables(body, parsePlainText) {
    const lines = (body || '').split('\n');
    const parts = [];
    let textBuffer = [];
    let tableBuffer = [];
    let pendingNoHeader = false;

    const flushTextBuffer = () => {
        if (textBuffer.length === 0) return;
        parts.push(...parsePlainText(textBuffer.join('\n')));
        textBuffer = [];
    };

    const flushUnusedNoHeaderMarker = () => {
        if (!pendingNoHeader || tableBuffer.length > 0) return;
        textBuffer.push(TABLE_NO_HEADER_MARKER);
        pendingNoHeader = false;
    };

    const flushTableBuffer = () => {
        if (tableBuffer.length === 0) return;

        const header = !pendingNoHeader;
        const keepMarker = pendingNoHeader;
        pendingNoHeader = false;

        if (tableBuffer.length < 2) {
            if (keepMarker) textBuffer.push(TABLE_NO_HEADER_MARKER);
            textBuffer.push(...tableBuffer);
            tableBuffer = [];
            return;
        }

        const rawRows = tableBuffer.map(parseTableRowCells);
        const dataRows = rawRows.filter(r => !isTableSeparatorRow(r));

        if (dataRows.length === 0) {
            if (keepMarker) textBuffer.push(TABLE_NO_HEADER_MARKER);
            textBuffer.push(...tableBuffer);
            tableBuffer = [];
            return;
        }

        parts.push({
            type: 'table',
            rows: dataRows.map((cells, rowIndex) => ({
                isHeader: header && rowIndex === 0,
                cells: cells.map((cellText) => parsePlainText(cellText)),
            })),
        });
        tableBuffer = [];
    };

    for (const line of lines) {
        const trimmed = line.trim();
        if (tableBuffer.length === 0 && isTableNoHeaderDirective(trimmed)) {
            flushTextBuffer();
            pendingNoHeader = true;
            continue;
        }
        if (pendingNoHeader && tableBuffer.length === 0 && trimmed === '') {
            continue;
        }
        if (isMarkdownTableLine(trimmed, tableBuffer.length > 0)) {
            flushTextBuffer();
            tableBuffer.push(line);
        } else {
            flushUnusedNoHeaderMarker();
            flushTableBuffer();
            textBuffer.push(line);
        }
    }
    flushUnusedNoHeaderMarker();
    flushTableBuffer();
    flushTextBuffer();

    return parts;
}

/**
 * Parses fill-gap exercise bodies with markdown tables preserved.
 * Gaps inside table cells stay inside their cell; table structure is not broken.
 *
 * @returns {Array<
 *   {type:'text', text:string} |
 *   {type:'gap', prompt:string, answers:string[]} |
 *   {type:'table', rows: Array<{ isHeader: boolean, cells: ReturnType<parseFillGapsPlainText>[] }>}
 * >}
 */
export function parseFillGapsStructuredBody(body) {
    return parseBodyWithMarkdownTables(body, parseFillGapsPlainText);
}

/** Flatten structured parts into text/gap sequence (document order, row-by-row within tables). */
export function flattenFillGapParts(parts) {
    const flat = [];
    for (const p of parts) {
        if (p.type === 'text' || p.type === 'gap') {
            flat.push(p);
        } else if (p.type === 'table') {
            for (const row of p.rows) {
                for (const cellParts of row.cells) {
                    flat.push(...cellParts);
                }
            }
        }
    }
    return flat;
}

/** Collect gap parts in document order from structured or flat parts. */
export function collectGapParts(parts) {
    return flattenFillGapParts(parts).filter(p => p.type === 'gap');
}

/** @returns {{type:'text'|'gap', text?:string, prompt?:string, answers?:string[]}[]} */
export function parseFillGapsBody(body) {
    return flattenFillGapParts(parseFillGapsStructuredBody(body));
}

function normalizeFillGapsWordsGapPart(part) {
    if (part.type === 'gap' && part.answers.length === 0 && part.prompt) {
        return { ...part, answers: [part.prompt], prompt: '' };
    }
    return part;
}

/** Normalize Given Words gaps (prompt-only → answer) in structured parts, including table cells. */
export function normalizeFillGapsWordsParts(parts) {
    return parts.map((p) => {
        if (p.type === 'table') {
            return {
                ...p,
                rows: p.rows.map((row) => ({
                    ...row,
                    cells: row.cells.map((cell) => cell.map(normalizeFillGapsWordsGapPart)),
                })),
            };
        }
        return normalizeFillGapsWordsGapPart(p);
    });
}

/**
 * True when the whole string is a single emphasis wrap (*italic* / **bold** / _…_ / __…__).
 * Used so a closing emphasis * is not mistaken for the MC “correct answer” marker.
 */
function isFullyWrappedEmphasis(s) {
    const t = String(s || '');
    if (/^\*\*([\s\S]+)\*\*$/.test(t)) return true;
    if (/^__([\s\S]+)__$/.test(t)) return true;
    if (/^\*([^*]+)\*$/.test(t)) return true;
    if (/^_([^_]+)_$/.test(t)) return true;
    return false;
}

/**
 * Parse one MC option: trailing `*` (or ` *`) marks correct.
 * Does not treat the closing `*` of `*italic*` / `**bold**` as the correct marker.
 * For italic + correct, use a space before the marker: `*No, I'm not.* *`
 * @returns {{ label: string, correct: boolean }}
 */
export function parseMcOptionText(raw) {
    let label = String(raw || '').trim();
    let correct = false;

    if (/\s\*$/.test(label)) {
        correct = true;
        label = label.replace(/\s\*$/, '').trim();
    } else if (label.endsWith('*') && !label.endsWith('**') && !isFullyWrappedEmphasis(label)) {
        correct = true;
        label = label.slice(0, -1).trim();
    }

    return { label, correct };
}

/** Plain text for native <option> / inputs that cannot render HTML. */
export function stripInlineMarkersPlain(text) {
    if (!text) return '';
    return String(text)
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/__(.+?)__/g, '$1')
        .replace(/\*(.+?)\*/g, '$1')
        .replace(/_(.+?)_/g, '$1')
        .replace(/\{c:[^}]+\}(.+?)\{\/c\}/g, '$1')
        .replace(/\{bg:[^}]+\}(.+?)\{\/bg\}/g, '$1')
        .replace(/\{s:[^}]+\}(.+?)\{\/s\}/g, '$1')
        .replace(/\|\|(.+?)\|\|/g, '$1')
        .replace(/<\/?u>/gi, '');
}

/** @returns {{type:'text'|'choice', text?:string, options?:object[], idx?:number}[]} */
export function parseMultipleChoiceShortBody(body) {
    const parts = [];
    let gapIdx = 0;
    for (const token of iterateBodyBrackets(body)) {
        if (token.type === 'text') {
            parts.push({ type: 'text', text: token.text });
        } else {
            const inner = token.inner || '';
            const options = inner.split('/').map((o) => parseMcOptionText(o));
            parts.push({ type: 'choice', options, idx: gapIdx++ });
        }
    }
    return peelEmphasisWrapAroundWidgets(parts);
}

export function countFillGapBrackets(body) {
    return parseFillGapsBody(body).filter(p => p.type === 'gap').length;
}

export function countMultipleChoiceShortBrackets(body) {
    return parseMultipleChoiceShortBody(body).filter(p => p.type === 'choice').length;
}

const GAP_PREFIX = /^gap:\s*/i;
const MULTIPLE_PREFIX = /^multiple:\s*/i;
const CHECKBOX_PREFIX = /^checkbox:\s*/i;
const NOTE_PREFIX = /^note:\s*/i;

function parseMixedCheckboxRest(rest) {
    const flag = String(rest ?? '').trim();
    if (flag === '+') return { type: 'check', expected: true };
    if (flag === '-') return { type: 'check', expected: false };
    if (flag === '') return { type: 'check', expected: null };
    return null;
}

/** `[note:anchor/tooltip]` — highlight preceding matching text; tooltip on click. */
function parseMixedNoteRest(rest) {
    const raw = String(rest ?? '');
    const slash = raw.indexOf('/');
    if (slash === -1) {
        const anchor = raw.trim();
        if (!anchor) return null;
        return { type: 'note', anchor, tooltip: '' };
    }
    const anchor = raw.slice(0, slash).trim();
    const tooltip = raw.slice(slash + 1).trim();
    if (!anchor) return null;
    return { type: 'note', anchor, tooltip };
}

/**
 * Peel `anchor` from the end of preceding text (trailing spaces kept after the highlight).
 * @returns {{ before: string, highlight: string, after: string } | null}
 */
export function peelNoteAnchorFromText(text, anchor) {
    const src = String(text ?? '');
    const a = String(anchor ?? '');
    if (!a || !src) return null;
    const trailingWs = (src.match(/\s*$/) || [''])[0];
    const core = src.slice(0, src.length - trailingWs.length);
    if (core.endsWith(a)) {
        return {
            before: core.slice(0, -a.length),
            highlight: a,
            after: trailingWs,
        };
    }
    const idx = core.lastIndexOf(a);
    if (idx === -1) return null;
    return {
        before: core.slice(0, idx),
        highlight: core.slice(idx, idx + a.length),
        after: core.slice(idx + a.length) + trailingWs,
    };
}

/**
 * Turn raw `{ type:'note', anchor, tooltip }` parts into highlighted spans by
 * peeling matching text from the previous text segment. The `[note:…]` marker
 * itself is never shown.
 */
export function attachMixedNotes(parts) {
    const out = [];
    for (const p of parts || []) {
        if (p?.type !== 'note') {
            out.push(p);
            continue;
        }
        const tooltip = p.tooltip ?? '';
        let prevIdx = out.length - 1;
        while (prevIdx >= 0 && out[prevIdx]?.type !== 'text') prevIdx -= 1;
        if (prevIdx >= 0) {
            const peeled = peelNoteAnchorFromText(out[prevIdx].text, p.anchor);
            if (peeled) {
                if (peeled.before) out[prevIdx] = { type: 'text', text: peeled.before };
                else out.splice(prevIdx, 1);
                out.push({ type: 'note', text: peeled.highlight, tooltip });
                if (peeled.after) out.push({ type: 'text', text: peeled.after });
                continue;
            }
        }
        // No match in previous text — still show the anchor as a highlight at the marker site.
        out.push({ type: 'note', text: p.anchor || '', tooltip });
    }
    return out;
}

function parseMixedBracketInner(inner) {
    const trimmed = String(inner || '').trim();
    if (GAP_PREFIX.test(trimmed)) {
        const rest = trimmed.replace(GAP_PREFIX, '');
        const { prompt, answers } = parseFillGapInner(rest);
        return { type: 'gap', prompt, answers };
    }
    if (MULTIPLE_PREFIX.test(trimmed)) {
        const rest = trimmed.replace(MULTIPLE_PREFIX, '');
        const options = rest.split('/').map((o) => parseMcOptionText(o));
        return { type: 'choice', options };
    }
    if (CHECKBOX_PREFIX.test(trimmed)) {
        const parsed = parseMixedCheckboxRest(trimmed.replace(CHECKBOX_PREFIX, ''));
        if (parsed) return parsed;
    }
    if (NOTE_PREFIX.test(trimmed)) {
        const parsed = parseMixedNoteRest(trimmed.replace(NOTE_PREFIX, ''));
        if (parsed) return parsed;
    }
    return { type: 'text', text: `[${inner}]` };
}

function parseFillGapsChoicePlainText(body) {
    const parts = [];
    for (const token of iterateBodyBrackets(body)) {
        if (token.type === 'text') {
            parts.push({ type: 'text', text: token.text });
        } else {
            parts.push(parseMixedBracketInner(token.inner));
        }
    }
    return attachMixedNotes(peelEmphasisWrapAroundWidgets(parts));
}

function assignMixedItemIndices(parts) {
    let idx = 0;
    const walk = (list) => (list || []).map((p) => {
        if (p.type === 'gap' || p.type === 'choice' || p.type === 'check') {
            return { ...p, idx: idx++ };
        }
        if (p.type === 'table') {
            return {
                ...p,
                rows: p.rows.map((row) => ({
                    ...row,
                    cells: row.cells.map((cell) => walk(cell)),
                })),
            };
        }
        return p;
    });
    return walk(parts);
}

/** Parse Mixed bodies (`[gap:…]`, `[multiple:…]`, `[checkbox:…]`, `[note:…]`). Tables preserved. */
export function parseFillGapsChoiceStructuredBody(body) {
    return assignMixedItemIndices(parseBodyWithMarkdownTables(body, parseFillGapsChoicePlainText));
}

export function collectMixedGapChoiceItems(parts) {
    const items = [];
    const walk = (list) => {
        for (const p of list || []) {
            if (p.type === 'gap' || p.type === 'choice' || p.type === 'check') items.push(p);
            else if (p.type === 'table') {
                for (const row of p.rows) {
                    for (const cell of row.cells) walk(cell);
                }
            }
        }
    };
    walk(parts);
    return items;
}

export function parseFillGapsChoiceBody(body) {
    return collectMixedGapChoiceItems(parseFillGapsChoiceStructuredBody(body));
}

export function countFillGapsChoiceItems(body) {
    return parseFillGapsChoiceBody(body).length;
}

function parseChecklistBracketInner(inner) {
    const trimmed = String(inner ?? '').trim();
    if (trimmed === '+') return { type: 'check', expected: true };
    if (trimmed === '-') return { type: 'check', expected: false };
    if (trimmed === '') return { type: 'check', expected: null };
    return { type: 'text', text: `[${inner}]` };
}

function parseChecklistPlainText(body) {
    const parts = [];
    for (const token of iterateBodyBrackets(body)) {
        if (token.type === 'text') parts.push({ type: 'text', text: token.text });
        else parts.push(parseChecklistBracketInner(token.inner));
    }
    return parts;
}

function assignChecklistIndices(parts) {
    let idx = 0;
    const walk = (list) => (list || []).map((p) => {
        if (p.type === 'check') return { ...p, idx: idx++ };
        if (p.type === 'table') {
            return {
                ...p,
                rows: p.rows.map((row) => ({
                    ...row,
                    cells: row.cells.map((cell) => walk(cell)),
                })),
            };
        }
        return p;
    });
    return walk(parts);
}

/** Parse Checklist bodies (`[+]`, `[-]`, `[]`). Tables preserved. Other `[brackets]` stay as text. */
export function parseChecklistStructuredBody(body) {
    return assignChecklistIndices(parseBodyWithMarkdownTables(body, parseChecklistPlainText));
}

export function collectChecklistItems(parts) {
    const items = [];
    const walk = (list) => {
        for (const p of list || []) {
            if (p.type === 'check') items.push(p);
            else if (p.type === 'table') {
                for (const row of p.rows) {
                    for (const cell of row.cells) walk(cell);
                }
            }
        }
    };
    walk(parts);
    return items;
}

export function parseChecklistBody(body) {
    return collectChecklistItems(parseChecklistStructuredBody(body));
}

export function checklistHasAutoKeys(body) {
    return parseChecklistBody(body).some((item) => item.expected === true || item.expected === false);
}

export function isChecklistBoxChecked(answers, idx) {
    const v = answers?.[idx] ?? answers?.[String(idx)];
    return v === true || v === 'true' || v === 1 || v === '1';
}

/** Gap metadata for open exercises (no text segments). */
export function extractGaps(body) {
    return parseFillGapsBody(body)
        .filter(p => p.type === 'gap')
        .map((g, id) => ({
            id,
            prompt: g.prompt,
            answers: g.answers,
        }));
}
